/**
 * The run page: one URL for a run's whole life (mission 6 T016; FR-011,
 * FR-012, FR-021, FR-030; research §1.4; sheet 13 callout 1).
 *
 * It reads the one polled `['run', id]` query (research §1.8) and renders by
 * state:
 * - queued, validating, generating, analyzing, failed, cancelled and any state
 *   the SPA does not know yet: InProgress (sheet 13; C10);
 * - rejected: Rejected (sheet 12);
 * - ready: Results (sheets 20 and 21), in place at the same URL.
 * On `/app/runs/:runId/report-rejection` it renders Results and the
 * ReportRejection modal, only at ready; before ready it goes back to the run,
 * so the findings views are never read early (C11).
 *
 * A malformed id is refused by the wrapper before any fetch and renders
 * not-found, as a 404 does (S6); polling stops on either (C10). Any other
 * failure that outlasts the poll's retries (lib/queries.ts) keeps the last
 * status on screen under a band with Retry, or, before a first answer, shows
 * the generic sentence with Retry: a live run never becomes a dead end
 * (mission 6 review follow-up 15, A-2).
 *
 * The one credit refresh for a cancel or any terminal state seen by polling
 * lives here: on an observed transition of the same run from a live state to a
 * terminal one, never on a terminal first load, including the first load of
 * another run after a :runId change (P9, NFR-005; lib/queries.ts).
 *
 * Screens receive the frozen RunScreenProps and never edit this file (T17).
 *
 * Mission 8 WP10 (T052; FR-020 A-L9) is the one follow-up that edits it, for
 * the rendering of the states this page draws itself: loading and a failed
 * first read keep the run label as the `h1` above their band; not-found has
 * `notFoundTitle` as its `h1` (Download renders it the same way). A screen
 * state adds no `h1`: the screen owns it (RunHeader). The poll and the credit
 * refresh are untouched.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import { Navigate, useParams } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import { Alert, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { ApiError, InvalidIdError } from '@/lib/api'
import { isTerminal, type RunState } from '@/lib/api/runs'
import { Mono } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { invalidateCredits, invalidateRun, runQuery } from '@/lib/queries'
import InProgress from './InProgress'
import Rejected from './Rejected'
import ReportRejection from './ReportRejection'
import Results from './Results'

export interface RunPageProps {
  mode?: 'report-rejection'
}

function isNotFound(error: unknown): boolean {
  return error instanceof InvalidIdError || (error instanceof ApiError && error.problem.status === 404)
}

/** A state this page draws itself: its `h1`, then the band (A-L9). */
function Titled({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-6">
      <h1 tabIndex={-1} className="text-h1 text-navy-950">
        {title}
      </h1>
      {children}
    </div>
  )
}

export default function RunPage({ mode }: RunPageProps) {
  const { runId = '' } = useParams()
  const qc = useQueryClient()
  const query = useQuery(runQuery(runId))
  const observedId = query.data?.run_id
  const state = query.data?.state

  // The last run and state this page observed. A live → terminal change of the
  // same run is the only credit trigger for a cancel or a polled terminal state
  // (P9). React Router keeps this page mounted across a :runId change, so the
  // run id is part of the record: arriving at another run that is already
  // terminal is a first load, not a transition (review cycle 1, finding 2).
  const observed = useRef<{ id: string; state: RunState } | undefined>(undefined)
  useEffect(() => {
    if (observedId === undefined || state === undefined) return
    const previous = observed.current
    observed.current = { id: observedId, state }
    if (previous?.id === observedId && !isTerminal(previous.state) && isTerminal(state)) void invalidateCredits(qc)
  }, [observedId, state, qc])

  const onRunChanged = useCallback(() => void invalidateRun(qc, runId), [qc, runId])

  const runTitle = <Mono>{PROVISIONAL_COPY.runLabel.render({ runId })}</Mono>
  if (query.status === 'pending') {
    return (
      <Titled title={runTitle}>
        <Alert variant="info" aria-busy="true">
          <AlertTitle>{copyText('loading')}</AlertTitle>
        </Alert>
      </Titled>
    )
  }
  const retryButton = (
    <Button variant="secondary" size="sm" loading={query.isFetching} onClick={() => void query.refetch()}>
      {copyText('retry')}
    </Button>
  )
  if (query.status === 'error' && (query.data === undefined || isNotFound(query.error))) {
    if (isNotFound(query.error)) {
      return (
        <Titled title={copyText('notFoundTitle')}>
          <Alert variant="blocked">
            <AlertTitle>{copyText('notFound')}</AlertTitle>
          </Alert>
        </Titled>
      )
    }
    return (
      <Titled title={runTitle}>
        <Band kind="blocked" action={retryButton}>
          <p>{copyText('genericError')}</p>
        </Band>
      </Titled>
    )
  }

  const run = query.data
  const interrupted =
    query.status === 'error' ? (
      <Band kind="warning" action={retryButton}>
        <p>{copyText('pollInterrupted')}</p>
      </Band>
    ) : null
  if (mode === 'report-rejection') {
    if (run.state !== 'ready') return <Navigate to={`/app/runs/${encodeURIComponent(run.run_id)}`} replace />
    return (
      <>
        {interrupted}
        <Results run={run} onRunChanged={onRunChanged} />
        <ReportRejection run={run} onRunChanged={onRunChanged} />
      </>
    )
  }
  let screen
  if (run.state === 'ready') screen = <Results run={run} onRunChanged={onRunChanged} />
  else if (run.state === 'rejected') screen = <Rejected run={run} onRunChanged={onRunChanged} />
  else screen = <InProgress run={run} onRunChanged={onRunChanged} />
  if (interrupted === null) return screen
  return (
    <div className="flex flex-col gap-4">
      {interrupted}
      {screen}
    </div>
  )
}
